/**
 * Helper to filter and find the nearest future contract from search results.
 * Supports MCX and NFO exchanges.
 * If symbolMatch is provided, it strictly filters by that symbol.
 */
export function findNearestFuture(results, symbolMatch) {
  const now = Math.trunc(Date.now() / 1000);
  const futures = (Array.isArray(results) ? results : []).filter((item) => {
    // Support MCX, NFO, and BFO exchanges
    const validExchanges = ['MCX', 'NFO', 'BFO'];
    if (!validExchanges.includes(item.exchange)) return false;
    
    // Check if it is a future contract
    const isFut = (item.trading_symbol || '').toUpperCase().endsWith('FUT');
    if (!isFut) return false;
    
    // Strict symbol matching if requested
    if (symbolMatch && item.symbol !== symbolMatch) return false;
    
    return true;
  });

  if (futures.length === 0) return null;

  const upcoming = futures
    .map((f) => ({ ...f, _expiryN: Number(f.expiry) }))
    .filter((f) => f._expiryN > now)
    .sort((a, b) => a._expiryN - b._expiryN);

  return upcoming.length > 0 ? upcoming[0] : futures[0];
}

/**
 * Format Unix timestamp to "18 May 2026"
 * Uses UTC to ensure the date component matches the exchange intended day.
 */
export function formatExpiry(unixSecs) {
  if (!unixSecs) return '';
  const d = new Date(Number(unixSecs) * 1000);
  return d.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * Format string "16JUN26" to "16 Jun 2026"
 */
export function formatOptionExpiry(dateStr) {
  if (!dateStr || dateStr.length < 7) {
    return dateStr;
  }
  const day = dateStr.substring(0, 2);
  let month = dateStr.substring(2, 5);
  month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
  const year = "20" + dateStr.substring(5, 7);
  return `${day} ${month} ${year}`;
}

/**
 * Shared helper: returns a smart label based on the calendar-day difference
 * between today and the given Date object.
 *
 * 0 days  → "Today"
 * 1 day   → "Tomorrow"
 * 2-6 days → "Wednesday, 30 Jul"
 * 7+ days → "30 Jul 2026"
 */
function getSmartLabel(expiryDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiry = new Date(expiryDate.getTime());
  expiry.setHours(0, 0, 0, 0);

  const diffMs = expiry.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';

  if (diffDays >= 2 && diffDays <= 6) {
    const dayName = expiryDate.toLocaleDateString('en-IN', { weekday: 'long' });
    const dateLabel = expiryDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    return `${dayName}, ${dateLabel}`;
  }

  // 7+ days: show full date with year
  return expiryDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Smart version of formatExpiry for unix timestamps.
 * Used by Nearest Future card.
 */
export function formatSmartExpiry(unixSecs) {
  if (!unixSecs) return '';
  const d = new Date(Number(unixSecs) * 1000);
  return getSmartLabel(d);
}

/**
 * Smart version of formatOptionExpiry for "28JUL26" strings.
 * Used by OPTIONS expiry label.
 */
export function formatSmartOptionExpiry(dateStr) {
  if (!dateStr || dateStr.length < 7) return dateStr;
  const day = parseInt(dateStr.substring(0, 2), 10);
  const monthStr = dateStr.substring(2, 5).toUpperCase();
  const year = 2000 + parseInt(dateStr.substring(5, 7), 10);

  const months = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };
  const monthIndex = months[monthStr];
  if (monthIndex === undefined) return dateStr;

  const d = new Date(year, monthIndex, day);
  return getSmartLabel(d);
}
